// import React, { useState, useEffect, useCallback, useRef } from 'react';
// import { Navigate } from 'react-router-dom';
// import { jwtDecode } from 'jwt-decode';
// import paths from './paths';
// import axios from "axios";

// const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minute for inactivity timeout
// const TOKEN_REFRESH_INTERVAL = 60 * 1000; // 1 minute for inactivity timeout

// const PrivateRoute = ({ children }) => {
//   const [isAuthenticated, setIsAuthenticated] = useState(true);
//   const inactivityTimerRef = useRef(null);
//   const lastActivityTimeRef = useRef(Date.now());

//   const handleLogout = useCallback(() => {
//     sessionStorage.removeItem('token');
//     sessionStorage.removeItem('refreshToken');
//     setIsAuthenticated(false);
//   }, []);

//   const refreshAccessToken = useCallback(async () => {
//     const refreshToken = localStorage.getItem('refreshToken')?.split(' ')[1];

//     if (!refreshToken) {
//       handleLogout();
//       return null;
//     }

//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_API_URL}/userRoute/refresh`,
//         { refreshToken }
//       );

//       if (response.status === 200) {
//         const { token, newRefreshToken } = response.data;
//         sessionStorage.setItem('token', token);
//         sessionStorage.setItem('refreshToken', newRefreshToken);
//         return token;
//       } else {
//         handleLogout();
//         return null;
//       }
//     } catch {
//       handleLogout();
//       return null;
//     }
//   }, [handleLogout]);

//   const resetInactivityTimer = useCallback(() => {
//     lastActivityTimeRef.current = Date.now();

//     if (inactivityTimerRef.current) {
//       clearTimeout(inactivityTimerRef.current);
//     }

//     inactivityTimerRef.current = setTimeout(() => {
//       handleLogout();
//     }, INACTIVITY_TIMEOUT);
//   }, [handleLogout]);

//   useEffect(() => {
//     let isMounted = true;

//     const checkToken = async () => {
//       const token = localStorage.getItem('token');

//       if (!token) {
//         if (isMounted) handleLogout();
//         return;
//       }

//       try {
//         const decodedToken = jwtDecode(token);
//         const currentTime = Date.now() / 1000;
//         if (decodedToken.exp < currentTime) {
//           const newToken = await refreshAccessToken();
//           if (!newToken && isMounted) {
//             handleLogout();
//           }
//         }
//       } catch {
//         if (isMounted) handleLogout();
//       }
//     };

//     checkToken();

//     const refreshInterval = setInterval(() => {
//       if (isMounted) refreshAccessToken();
//     }, TOKEN_REFRESH_INTERVAL);

//     const eventTypes = ['mousemove', 'keydown', 'keypress', 'keyup', 'scroll', 'click'];
//     eventTypes.forEach(event =>
//       window.addEventListener(event, resetInactivityTimer)
//     );

//     resetInactivityTimer();

//     return () => {
//       isMounted = false;
//       clearInterval(refreshInterval);
//       clearTimeout(inactivityTimerRef.current);
//       eventTypes.forEach(event =>
//         window.removeEventListener(event, resetInactivityTimer)
//       );
//     };
//   }, [refreshAccessToken, resetInactivityTimer, handleLogout]);

//   if (!isAuthenticated) {
//     return <Navigate to={paths.login} replace />;
//   }

//   return children;
// };

// export default PrivateRoute;

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import paths from './paths';
import axios from 'axios';
import { disconnectSocket } from 'assets/js/common';

const BLOCKED_STATUSES = ["ACCOUNT_OFFBOARDED", "PAYMENT_REQUIRED_EMPLOYEE_BLOCKED", "SUBSCRIPTION_EXPIRED"];

const ALLOWED_WHEN_BLOCKED = [
  paths.renewSubscriptionRoot,
  paths.paymentResult.split("/:")[0],
  paths.mySubscription,
  paths.login,
  "/dashboard",
  "/client/dashboard",
  "/employee/dashboard",
];

const getDashboardPath = () => {
  const userRoleName = localStorage.getItem("UserRole")?.toLowerCase();
  const isMedewerker = localStorage.getItem("isMedewerker") === "true";
  if (userRoleName === "client") return "/client/dashboard";
  if (isMedewerker) return "/employee/dashboard";
  return "/dashboard";
};

const PrivateRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
      const userId = localStorage.getItem("UserId");

      if (!isLoggedIn || !userId) {
        navigate(paths.login, { replace: true });
        return;
      }

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/validateSession/${userId}`,
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );

        if (cancelled) return;

        if (res.data.valid === false) {
          const reason = res.data.reason;

          if (reason === "SUBSCRIPTION_EXPIRED_EMPLOYEE_BLOCKED") {
            toast.error(t("subscriptionExpiredEmployeeBlocked"), { position: "top-right", autoClose: 6000 });
            disconnectSocket();
            localStorage.clear();
            navigate(paths.login, { replace: true });
            return;
          }

          if (reason === "SUBSCRIPTION_EXPIRED" && localStorage.getItem("subscriptionExpiredHandled") !== "true") {
            toast.error(t("subscriptionExpiredLoggedOut"), { position: "top-right", autoClose: 6000 });
            disconnectSocket();
            localStorage.clear();
            localStorage.setItem("subscriptionExpiredHandled", "true");
            navigate(paths.login, { replace: true });
            return;
          }

          // Subscription-related blocked reasons -> restrict to allowed paths
          if (BLOCKED_STATUSES.includes(reason)) {
            localStorage.setItem("subscriptionStatus", reason);

            const isOnAllowedPath = ALLOWED_WHEN_BLOCKED.some(p =>
              location.pathname === p || location.pathname.startsWith(p + "/")
            );

            if (!isOnAllowedPath) {
              navigate(getDashboardPath(), { replace: true });
              return;
            }

            setIsAuthChecked(true);
            return;
          }

          // Any other invalid reason (USER_DELETED, USER_INACTIVE, ACCOUNT_DEACTIVATED, USER_NOT_FOUND, etc.)
          // -> session is genuinely invalid, log the user out
          disconnectSocket();
          localStorage.clear();
          navigate(paths.login, { replace: true });
          return;
        }

        localStorage.setItem("subscriptionStatus", "Active");
        localStorage.removeItem("subscriptionExpiredHandled");
        setIsAuthChecked(true);
      } catch (err) {
        console.error("Session validation failed in PrivateRoute:", err);
        if (!cancelled) setIsAuthChecked(true);
      }
    };

    check();

    return () => {
      cancelled = true;
    };
  }, [navigate, location.pathname]);

  if (!isAuthChecked) {
    return null;
  }

  return children;
};

export default PrivateRoute;